import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  type ControllerRenderProps,
  type DefaultValues,
  type FieldValues,
  useForm,
  type Path,
} from "react-hook-form";
import type { ZodType } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { t } from "i18next";

interface SingleFormDialogProps<T extends FieldValues> {
  title: string;
  schema: ZodType<T>;
  defaultValues?: DefaultValues<T>;
  name: Path<T>;
  trigger: React.ReactNode;
  onSubmit: (data: T) => void;
  render: (field: ControllerRenderProps<T, Path<T>>) => JSX.Element;
}

export const SingleFormDialog = <T extends FieldValues>(
  props: SingleFormDialogProps<T>
): JSX.Element => {
  const [open, setOpen] = useState(false);

  const form = useForm<T>({
    resolver: zodResolver(props.schema),
    defaultValues: props.defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(props.defaultValues);
    }
  }, [open, props.defaultValues, form]);

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSubmit = (data: T): void => {
    props.onSubmit(data);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{props.trigger}</DialogTrigger>
      <DialogDescription className="sr-only">{props.name}</DialogDescription>
      <DialogContent aria-describedby={props.name}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name={props.name}
              render={({ field }) => props.render(field)}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">
                  {t("settings.profile.cancelModal")}
                </Button>
              </DialogClose>
              <Button type="submit" variant="default">
                {t("settings.profile.saveModal")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
